/**
 * Insights service: get cached insights per section (010)
 * Contract: getCachedInsights returns sections with category, items, refreshed_at, time_range_days.
 */

import { supabase } from '../lib/supabase';
import type { CachedInsightSection, InsightCategory, InsightScanSummary } from '../types/insights.types';

const CATEGORIES: InsightCategory[] = ['alerts', 'progress', 'opportunities', 'analysis'];

export async function getCachedInsights(userId: string): Promise<CachedInsightSection[]> {
  const sections: CachedInsightSection[] = [];

  for (const category of CATEGORIES) {
    const { data, error } = await (supabase as any)
      .from('user_insights')
      .select('payload, refreshed_at, time_range_days')
      .eq('user_id', userId)
      .eq('category', category)
      .maybeSingle();

    if (error) {
      sections.push({
        category,
        items: [],
        refreshed_at: null,
        time_range_days: 90,
      });
      continue;
    }

    const payload = data?.payload as { items?: unknown; scan_summary?: InsightScanSummary } | undefined;
    const items = payload?.items ?? [];
    const scanSummary =
      category === 'analysis' && payload?.scan_summary ? payload.scan_summary : undefined;

    sections.push({
      category,
      items: Array.isArray(items) ? items : [],
      refreshed_at: data?.refreshed_at ?? null,
      time_range_days: typeof data?.time_range_days === 'number' ? data.time_range_days : 90,
      scan_summary: scanSummary ?? null,
    });
  }

  return sections;
}

export async function getCachedSection(
  userId: string,
  category: InsightCategory
): Promise<CachedInsightSection> {
  const { data, error } = await (supabase as any)
    .from('user_insights')
    .select('payload, refreshed_at, time_range_days')
    .eq('user_id', userId)
    .eq('category', category)
    .maybeSingle();

  if (error || !data) {
    return {
      category,
      items: [],
      refreshed_at: null,
      time_range_days: 90,
    };
  }

  const payload = data.payload as { items?: unknown; scan_summary?: InsightScanSummary } | undefined;
  const items = payload?.items ?? [];
  return {
    category,
    items: Array.isArray(items) ? items : [],
    refreshed_at: data.refreshed_at ?? null,
    time_range_days: typeof data.time_range_days === 'number' ? data.time_range_days : 90,
    scan_summary:
      category === 'analysis' && payload?.scan_summary ? payload.scan_summary : null,
  };
}
